<template>
  <div data-test="select-project">
    <div v-if="is_dropdown">
      <label
        v-if="label"
        class="label"
      >
        {{ label }}
      </label>
      <SearchAutoComplete
        ref="autocomplete_ref"
        v-model="project_name"
        :suggestions="suggestions_filtered"
        optionLabel="attributes.name"
        :appendTo="append_to_body ? 'body' : 'self'"
        :placeholder="$t('project.labels.searchProject')"
        :showClear="true"
        :pt="{
          pcInputText: {
            root: { 'data-test': 'select-project-input' },
          },
        }"
        completeOnFocus
        inputClass="w-full"
        class="h-9 w-full"
        @update:modelValue="onModelValueUpdate"
        @complete="onComplete"
        @option-select="(event) => selectProject(event.value)"
        @clear="clearProject"
      >
        <template #empty>
          <div
            data-test="empty-result"
            class="p-2 text-sm text-surface-500"
          >
            <div v-if="is_searching">
              {{ $t('select.actions.noResults') }} <span v-if="project_name">"{{ project_name }}"</span>
            </div>
            <div v-else>
              {{ $t('select.actions.startTyping') }}
            </div>
          </div>
        </template>
      </SearchAutoComplete>
    </div>
    <SearchInput
      v-else
      ref="input_ref"
      v-model="project_name"
      class="w-full"
      :placeholder="$t('project.labels.searchProject')"
      @update:model-value="searchProject"
    >
      <InputIcon
        v-if="project_name"
        class="cursor-pointer"
        @click="clearProject"
      >
        <FontAwesomeIcon
          :icon="['far', 'xmark']"
          size="sm"
        />
      </InputIcon>
    </SearchInput>
  </div>
</template>

<script setup>
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useToasts } from '@/app/util/toasts';
import SearchAutoComplete from '@/app/shared_components/inputs/SearchAutoComplete.vue';
import SearchInput from '@/app/shared_components/inputs/SearchInput.vue';
import _ from 'lodash-es';

const input_ref = ref(null);
const autocomplete_ref = ref(null);

onMounted(() => {
  if (props.autofocus) {
    nextTick(() => {
      if (props.is_dropdown) {
        autocomplete_ref.value.focus();
      } else {
        input_ref.value.focus();
      }
    });
  }
});

const toast_action_failed = useToasts().toast_action_failed;

const props = defineProps({
  active: { type: Object, default: null },
  append_to_body: { type: Boolean },
  label: { type: String, default: null },
  projects_props: { type: Object, default: null },
  is_dropdown: { type: Boolean, default: true },
  autofocus: { type: Boolean },
});

const emit = defineEmits(['onSearch', 'changeSelect']);

const store = useStore();
const project_name = ref(props.active ? props.active.attributes.name : '');
const is_searching = ref(false);
const projects_search = ref({});

const projects = computed(() => {
  if (props.projects_props) {
    return props.projects_props;
  }
  return projects_search.value;
});

const suggestions_filtered = computed(() =>
  _.filter(projects.value, (p) => {
    const search = typeof project_name.value === 'string' ? project_name.value : '';
    return p.attributes.name.toLowerCase().indexOf(search.toLowerCase()) >= 0;
  }),
);

watch(
  () => props.active,
  (newVal) => {
    project_name.value = _.get(newVal, 'attributes.name', '');
  },
);

const autocompleteProjects = _.debounce(async (search = '') => {
  try {
    projects_search.value = _.keyBy(
      await store.dispatch('AUTOCOMPLETE_PROJECTS', {
        name_cont: search,
      }),
      'id',
    );
  } catch (error) {
    toast_action_failed({ error: error });
  }
}, 100);

const onComplete = (event) => {
  searchProject(event.query);
};

const searchProject = (query) => {
  if (query.length >= 1) {
    is_searching.value = true;
    autocompleteProjects(query);
  } else {
    is_searching.value = false;
  }
  emit('onSearch', query);
};

const selectProject = (project) => {
  emit('changeSelect', project);
};

const onModelValueUpdate = (value) => {
  if (_.isNil(value)) {
    clearProject();
  }
};

const clearProject = () => {
  project_name.value = '';
  is_searching.value = false;
  selectProject(null);
};

defineExpose({ reset: clearProject });
</script>
